import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import {
  intakeApiClient,
  useArchiveIntakeMutation,
  useUnarchiveIntakeMutation,
} from '@/services/api/intakeService';

type UseIntakeArchiveProps = {
  /**
   * ID of the intake we want to archive / un-archive. If null the helper
   * functions will no-op and return disabled states.
   */
  intakeId: string | null;
  /**
   * Whether the intake is currently archived. Used to decide which mutation to
   * trigger when toggling the archive state.
   */
  isArchived: boolean;
};

export const useIntakeArchive = ({
  intakeId,
  isArchived,
}: UseIntakeArchiveProps) => {
  const dispatch = useDispatch();

  // RTK Query mutations
  const [archiveIntake, { isLoading: isArchiving }] =
    useArchiveIntakeMutation();
  const [unarchiveIntake] = useUnarchiveIntakeMutation();

  const handleArchiveToggle = useCallback(async () => {
    if (!intakeId) return;

    try {
      if (isArchived) {
        await unarchiveIntake(intakeId).unwrap();
      } else {
        await archiveIntake(intakeId).unwrap();
      }

      // Ensure any tables or queries listing intakes pick up the latest status
      dispatch(intakeApiClient.util.invalidateTags(['Intakes']));
    } catch (err) {
      /* eslint-disable no-console */
      console.error('Failed to toggle archive state for intake', err);
    }
  }, [intakeId, isArchived, archiveIntake, unarchiveIntake, dispatch]);

  return {
    isArchived,
    isArchiving,
    handleArchiveToggle,
  } as const;
};
